import { useEffect, useMemo, useRef, useState } from "react"
import { cn } from "@/shared/lib/utils"
import { Skeleton } from "@/shared/ui/skeleton"
import { Disclosure } from "@/shared/ui/disclosure"

/**
 * Категориальные цвета для тёмной поверхности, в фиксированном порядке. Проверены
 * валидатором на фоне карточки #2a2a2a: различимы и при нарушениях цветовосприятия.
 */
export const SERIES_COLORS = ["#3987e5", "#d95926", "#199e70"] as const

export interface LineSeries<T> {
	key: keyof T & string
	label: string
}

const HEIGHT = 220
const PAD = { top: 12, right: 12, bottom: 26, left: 40 }

function niceMax(value: number): number {
	if (value <= 4) return 4
	const power = 10 ** Math.floor(Math.log10(value))
	const step = [1, 2, 2.5, 5, 10].find((candidate) => candidate * power >= value / 4)! * power
	return Math.ceil(value / step) * step
}

const number = new Intl.NumberFormat("ru-RU")

/**
 * Линии во времени на одной оси: одна единица измерения на график. Наведение
 * показывает вертикаль и подсказку со всеми рядами в этой точке.
 */
export function LineChart<T extends { date: string }>({
	data,
	series,
	formatDate,
	className,
	ariaLabel,
}: {
	data: T[]
	series: LineSeries<T>[]
	formatDate: (date: string) => string
	className?: string
	ariaLabel: string
}) {
	const box = useRef<HTMLDivElement>(null)
	const [width, setWidth] = useState(600)
	const [hover, setHover] = useState<number | null>(null)

	useEffect(() => {
		const element = box.current
		if (!element) return
		const observer = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width))
		observer.observe(element)
		return () => observer.disconnect()
	}, [])

	const max = useMemo(
		() =>
			niceMax(
				Math.max(0, ...data.flatMap((point) => series.map((item) => Number(point[item.key]) || 0))),
			),
		[data, series],
	)

	const innerWidth = Math.max(10, width - PAD.left - PAD.right)
	const innerHeight = HEIGHT - PAD.top - PAD.bottom
	const x = (index: number) =>
		PAD.left + (data.length <= 1 ? innerWidth / 2 : (index / (data.length - 1)) * innerWidth)
	const y = (value: number) => PAD.top + innerHeight - (value / max) * innerHeight
	const ticks = [0, 0.25, 0.5, 0.75, 1].map((share) => share * max)
	const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(innerWidth / 80))))

	const onMove = (event: React.PointerEvent<SVGRectElement>) => {
		if (data.length === 0) return
		const rect = event.currentTarget.getBoundingClientRect()
		const share = (event.clientX - rect.left) / rect.width
		setHover(Math.min(data.length - 1, Math.max(0, Math.round(share * (data.length - 1)))))
	}

	const point = hover === null ? null : data[hover]
	const tipLeft = hover === null ? 0 : x(hover)

	return (
		<div className={cn("space-y-3", className)}>
			<ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-hidden>
				{series.map((item, index) => (
					<li key={item.key} className="flex items-center gap-1.5">
						<span
							className="h-0.5 w-3.5 rounded-full"
							style={{ background: SERIES_COLORS[index] }}
						/>
						{item.label}
					</li>
				))}
			</ul>
			<div ref={box} className="relative">
				<svg
					width={width}
					height={HEIGHT}
					role="img"
					aria-label={ariaLabel}
					className="block overflow-visible"
				>
					{ticks.map((tick) => (
						<g key={tick}>
							<line
								x1={PAD.left}
								x2={PAD.left + innerWidth}
								y1={y(tick)}
								y2={y(tick)}
								stroke="var(--color-border)"
								strokeWidth={1}
							/>
							<text
								x={PAD.left - 8}
								y={y(tick)}
								dy="0.32em"
								textAnchor="end"
								className="fill-muted-foreground text-[11px] tabular-nums"
							>
								{number.format(Math.round(tick))}
							</text>
						</g>
					))}
					{data.map((item, index) =>
						index % labelEvery === 0 ? (
							<text
								key={item.date}
								x={x(index)}
								y={HEIGHT - 6}
								textAnchor="middle"
								className="fill-muted-foreground text-[11px]"
							>
								{formatDate(item.date)}
							</text>
						) : null,
					)}
					{series.map((item, index) => (
						<polyline
							key={item.key}
							fill="none"
							stroke={SERIES_COLORS[index]}
							strokeWidth={2}
							strokeLinejoin="round"
							strokeLinecap="round"
							points={data
								.map((entry, at) => `${x(at)},${y(Number(entry[item.key]) || 0)}`)
								.join(" ")}
						/>
					))}
					{point && hover !== null ? (
						<g>
							<line
								x1={tipLeft}
								x2={tipLeft}
								y1={PAD.top}
								y2={PAD.top + innerHeight}
								stroke="var(--color-muted-foreground)"
								strokeWidth={1}
								opacity={0.5}
							/>
							{series.map((item, index) => (
								<circle
									key={item.key}
									cx={tipLeft}
									cy={y(Number(point[item.key]) || 0)}
									r={4}
									fill={SERIES_COLORS[index]}
									stroke="var(--color-card)"
									strokeWidth={2}
								/>
							))}
						</g>
					) : null}
					<rect
						x={PAD.left}
						y={PAD.top}
						width={innerWidth}
						height={innerHeight}
						fill="transparent"
						onPointerMove={onMove}
						onPointerLeave={() => setHover(null)}
					/>
				</svg>
				{point && hover !== null ? (
					<div
						className="pointer-events-none absolute top-2 z-10 min-w-40 rounded-xl border border-border bg-popover px-3 py-2 text-xs shadow-lg"
						style={{
							left: tipLeft,
							transform: `translateX(${tipLeft > width / 2 ? "calc(-100% - 12px)" : "12px"})`,
						}}
					>
						<p className="mb-1.5 font-medium">{formatDate(point.date)}</p>
						{series.map((item, index) => (
							<p key={item.key} className="flex items-center justify-between gap-4">
								<span className="flex items-center gap-1.5 text-muted-foreground">
									<span
										className="size-2 rounded-full"
										style={{ background: SERIES_COLORS[index] }}
									/>
									{item.label}
								</span>
								<span className="font-medium tabular-nums">
									{number.format(Number(point[item.key]) || 0)}
								</span>
							</p>
						))}
					</div>
				) : null}
			</div>
			<Disclosure title="Таблица" titleClassName="text-xs select-none">
				<div className="max-h-56 overflow-y-auto text-xs text-muted-foreground">
					<table className="w-full text-left tabular-nums">
						<thead>
							<tr>
								<th className="py-1 font-medium">Дата</th>
								{series.map((item) => (
									<th key={item.key} className="py-1 text-right font-medium">
										{item.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{data.map((entry) => (
								<tr key={entry.date} className="border-t border-border/60">
									<td className="py-1">{formatDate(entry.date)}</td>
									{series.map((item) => (
										<td key={item.key} className="py-1 text-right text-foreground">
											{number.format(Number(entry[item.key]) || 0)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Disclosure>
		</div>
	)
}

/** Заглушка графика: подписи рядов, поле графика той же высоты и строка «Таблица». */
export function LineChartSkeleton({
	series = 2,
	className,
}: {
	series?: number
	className?: string
}) {
	return (
		<div aria-hidden="true" className={cn("space-y-3", className)}>
			<div className="flex h-4 items-center gap-4">
				{Array.from({ length: series }, (_, index) => (
					<Skeleton key={index} className="h-2.5 w-36 rounded" />
				))}
			</div>
			<Skeleton className="w-full rounded-xl" style={{ height: HEIGHT }} />
			<div className="flex h-4 items-center">
				<Skeleton className="h-2.5 w-16 rounded" />
			</div>
		</div>
	)
}
